'use client';

import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// Types
// ============================================================================

export interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  shortcut?: string[];
  action?: () => void;
  disabled?: boolean;
  group?: string;
}

export interface CommandGroup {
  id: string;
  label: string;
  items: CommandItem[];
}

// ============================================================================
// Context
// ============================================================================

interface CommandContextValue {
  search: string;
  setSearch: (value: string) => void;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  flatItems: CommandItem[];
  onSelect: (item: CommandItem) => void;
}

const CommandContext = React.createContext<CommandContextValue | null>(null);

function _useCommandContext() {
  const context = React.useContext(CommandContext);
  if (!context) {
    throw new Error('Command components must be used within CommandPalette');
  }
  return context;
}

// ============================================================================
// CommandPalette
// ============================================================================

export interface CommandPaletteProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  items?: CommandItem[];
  groups?: CommandGroup[];
  placeholder?: string;
  emptyMessage?: string;
  onSelect?: (item: CommandItem) => void;
  filterFn?: (item: CommandItem, search: string) => boolean;
  children?: React.ReactNode;
}

export function CommandPalette({
  open = false,
  onOpenChange,
  items = [],
  groups = [],
  placeholder = 'Type a command or search...',
  emptyMessage = 'No results found.',
  onSelect,
  filterFn,
  children,
  className,
  ...props
}: CommandPaletteProps) {
  const [search, setSearch] = React.useState('');
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Combine items from both props and groups
  const allItems = React.useMemo(() => {
    const fromGroups = groups.flatMap((g) => g.items.map((item) => ({ ...item, group: g.label })));
    return [...items, ...fromGroups];
  }, [items, groups]);

  // Filter items based on search
  const filteredItems = React.useMemo(() => {
    if (!search) return allItems;

    const defaultFilter = (item: CommandItem, query: string) =>
      item.label.toLowerCase().includes(query.toLowerCase()) ||
      item.description?.toLowerCase().includes(query.toLowerCase());

    const filter = filterFn || defaultFilter;
    return allItems.filter((item) => filter(item, search));
  }, [allItems, search, filterFn]);

  // Handle keyboard navigation
  React.useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredItems.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredItems[selectedIndex] && !filteredItems[selectedIndex].disabled) {
            handleSelect(filteredItems[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onOpenChange?.(false);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, filteredItems, selectedIndex, onOpenChange]);

  // Reset selection when search changes
  React.useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Focus input when opened
  React.useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      setSearch('');
      setSelectedIndex(0);
    }
  }, [open]);

  const handleSelect = (item: CommandItem) => {
    item.action?.();
    onSelect?.(item);
    onOpenChange?.(false);
  };

  if (!open) return null;

  const contextValue: CommandContextValue = {
    search,
    setSearch,
    selectedIndex,
    setSelectedIndex,
    flatItems: filteredItems,
    onSelect: handleSelect,
  };

  return (
    <CommandContext.Provider value={contextValue}>
      <div
        className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]"
        onClick={() => onOpenChange?.(false)}
      >
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
          className={clsx(
            'command-palette relative w-full max-w-xl',
            'bg-white dark:bg-gray-800',
            'border border-gray-200 dark:border-gray-700',
            'rounded-xl shadow-2xl overflow-hidden',
            className
          )}
          {...props}
        >
          {children || (
            <>
              <CommandInput ref={inputRef} placeholder={placeholder} />
              <CommandList>
                {filteredItems.length === 0 ? (
                  <CommandEmpty>{emptyMessage}</CommandEmpty>
                ) : groups.length > 0 ? (
                  // Group-based rendering
                  groups.map((group) => {
                    const groupItems = filteredItems.filter((item) => item.group === group.label);
                    if (groupItems.length === 0) return null;
                    return (
                      <CommandGroupComponent key={group.id} heading={group.label}>
                        {groupItems.map((item) => (
                          <CommandItemComponent key={item.id} item={item} />
                        ))}
                      </CommandGroupComponent>
                    );
                  })
                ) : (
                  // Flat rendering
                  filteredItems.map((item) => (
                    <CommandItemComponent key={item.id} item={item} />
                  ))
                )}
              </CommandList>
            </>
          )}
        </div>
      </div>
    </CommandContext.Provider>
  );
}

// ============================================================================
// CommandInput
// ============================================================================

export interface CommandInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value?: string;
  onChange?: (value: string) => void;
}

export const CommandInput = React.forwardRef<HTMLInputElement, CommandInputProps>(
  function CommandInput({ value, onChange, className, ...props }, ref) {
    const context = React.useContext(CommandContext);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (context) {
        context.setSearch(newValue);
      }
      onChange?.(newValue);
    };

    return (
      <div className="flex items-center gap-2 px-3 border-b border-gray-200 dark:border-gray-700">
        <svg
          className="w-5 h-5 text-gray-400 dark:text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          ref={ref}
          type="text"
          value={value ?? context?.search ?? ''}
          onChange={handleChange}
          className={clsx(
            'flex-1 py-3 bg-transparent',
            'text-gray-900 dark:text-gray-100',
            'placeholder-gray-400 dark:placeholder-gray-500',
            'focus:outline-none',
            className
          )}
          {...props}
        />
      </div>
    );
  }
);

// ============================================================================
// CommandList
// ============================================================================

export interface CommandListProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function CommandList({ children, className, ...props }: CommandListProps) {
  return (
    <div
      role="listbox"
      className={clsx('command-list max-h-80 overflow-auto p-2', className)}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// CommandEmpty
// ============================================================================

export interface CommandEmptyProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function CommandEmpty({ children, className, ...props }: CommandEmptyProps) {
  return (
    <div
      className={clsx(
        'py-6 text-center text-sm text-gray-500 dark:text-gray-400',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// CommandGroup
// ============================================================================

export interface CommandGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  heading?: string;
  children: React.ReactNode;
}

function CommandGroupComponent({ heading, children, className, ...props }: CommandGroupProps) {
  return (
    <div className={clsx('command-group', className)} {...props}>
      {heading && (
        <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">
          {heading}
        </div>
      )}
      {children}
    </div>
  );
}

export { CommandGroupComponent as CommandGroup };

// ============================================================================
// CommandItem
// ============================================================================

export interface CommandItemComponentProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  item: CommandItem;
  onSelect?: (item: CommandItem) => void;
}

function CommandItemComponent({
  item,
  onSelect,
  className,
  ...props
}: CommandItemComponentProps) {
  const context = React.useContext(CommandContext);
  const index = context?.flatItems.findIndex((i) => i.id === item.id) ?? -1;
  const isSelected = context?.selectedIndex === index;

  const handleClick = () => {
    if (item.disabled) return;
    item.action?.();
    onSelect?.(item);
    context?.onSelect(item);
  };

  const handleMouseEnter = () => {
    if (context && index >= 0) {
      context.setSelectedIndex(index);
    }
  };

  return (
    <div
      role="option"
      aria-selected={isSelected}
      aria-disabled={item.disabled}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      className={clsx(
        'command-item flex items-center gap-3 px-2 py-2 rounded-lg cursor-pointer',
        isSelected && 'bg-gray-100 dark:bg-gray-700',
        item.disabled && 'opacity-50 cursor-not-allowed',
        !item.disabled && !isSelected && 'hover:bg-gray-50 dark:hover:bg-gray-700/50',
        className
      )}
      {...props}
    >
      {item.icon && (
        <span className="flex-shrink-0 w-5 h-5 text-gray-400 dark:text-gray-500">{item.icon}</span>
      )}

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.label}</p>
        {item.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {item.description}
          </p>
        )}
      </div>

      {item.shortcut && item.shortcut.length > 0 && (
        <div className="flex items-center gap-1">
          {item.shortcut.map((key, i) => (
            <kbd
              key={i}
              className={clsx(
                'px-1.5 py-0.5 text-xs font-medium',
                'bg-gray-100 dark:bg-gray-700',
                'border border-gray-200 dark:border-gray-600',
                'rounded'
              )}
            >
              {key}
            </kbd>
          ))}
        </div>
      )}
    </div>
  );
}

export { CommandItemComponent as CommandItem };

// ============================================================================
// CommandSeparator
// ============================================================================

export interface CommandSeparatorProps extends React.HTMLAttributes<HTMLDivElement> {}

export function CommandSeparator({ className, ...props }: CommandSeparatorProps) {
  return (
    <div
      className={clsx('h-px bg-gray-200 dark:bg-gray-700 my-1', className)}
      {...props}
    />
  );
}

// ============================================================================
// Spotlight
// ============================================================================

export interface SpotlightProps extends CommandPaletteProps {
  trigger?: React.ReactNode;
  hotkey?: string;
}

export function Spotlight({
  trigger,
  hotkey = 'k',
  open: controlledOpen,
  onOpenChange,
  ...props
}: SpotlightProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const handleOpenChange = (newOpen: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  // Register hotkey
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === hotkey.toLowerCase()) {
        e.preventDefault();
        handleOpenChange(!open);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hotkey, open]);

  return (
    <>
      {trigger && (
        <div onClick={() => handleOpenChange(true)}>{trigger}</div>
      )}
      <CommandPalette open={open} onOpenChange={handleOpenChange} {...props} />
    </>
  );
}

// ============================================================================
// QuickActions
// ============================================================================

export interface QuickActionsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  actions: CommandItem[];
  columns?: 1 | 2 | 3 | 4;
  onSelect?: (action: CommandItem) => void;
}

export function QuickActions({
  actions,
  columns = 3,
  onSelect,
  className,
  ...props
}: QuickActionsProps) {
  const columnClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
  };

  return (
    <div
      className={clsx('quick-actions grid gap-2', columnClasses[columns], className)}
      {...props}
    >
      {actions.map((action) => (
        <button
          key={action.id}
          type="button"
          onClick={() => {
            if (!action.disabled) {
              action.action?.();
              onSelect?.(action);
            }
          }}
          disabled={action.disabled}
          className={clsx(
            'flex flex-col items-center gap-2 p-4 rounded-lg',
            'bg-white dark:bg-gray-800',
            'border border-gray-200 dark:border-gray-700',
            'transition-colors',
            action.disabled
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-gray-50 dark:hover:bg-gray-700'
          )}
        >
          {action.icon && (
            <span className="w-8 h-8 text-gray-600 dark:text-gray-300">
              {action.icon}
            </span>
          )}
          <span className="text-sm font-medium text-center">{action.label}</span>
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// SearchCommand (Simplified command input)
// ============================================================================

export interface SearchCommandProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  placeholder?: string;
  items: CommandItem[];
  onSelect?: (item: CommandItem) => void;
  emptyMessage?: string;
}

export function SearchCommand({
  placeholder = 'Search...',
  items,
  onSelect,
  emptyMessage = 'No results found.',
  className,
  ...props
}: SearchCommandProps) {
  const [search, setSearch] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const filteredItems = React.useMemo(() => {
    if (!search) return items;
    const lowerSearch = search.toLowerCase();
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(lowerSearch) ||
        item.description?.toLowerCase().includes(lowerSearch)
    );
  }, [items, search]);

  React.useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  const handleSelect = (item: CommandItem) => {
    if (item.disabled) return;
    item.action?.();
    onSelect?.(item);
    setSearch('');
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredItems.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredItems[selectedIndex]) {
          handleSelect(filteredItems[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  return (
    <div className={clsx('search-command relative', className)} {...props}>
      <div className="relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={clsx(
            'w-full pl-10 pr-4 py-2',
            'border border-gray-300 dark:border-gray-600 rounded-lg',
            'bg-white dark:bg-gray-800',
            'focus:outline-none focus:ring-2 focus:ring-blue-500'
          )}
        />
      </div>

      {isOpen && search && (
        <div
          className={clsx(
            'absolute z-50 w-full mt-1',
            'bg-white dark:bg-gray-800',
            'border border-gray-200 dark:border-gray-700',
            'rounded-lg shadow-lg',
            'max-h-60 overflow-auto'
          )}
        >
          {filteredItems.length === 0 ? (
            <div className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
              {emptyMessage}
            </div>
          ) : (
            filteredItems.map((item, index) => (
              <div
                key={item.id}
                role="option"
                aria-selected={selectedIndex === index}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 cursor-pointer',
                  selectedIndex === index && 'bg-gray-100 dark:bg-gray-700',
                  item.disabled && 'opacity-50 cursor-not-allowed',
                  !item.disabled &&
                    selectedIndex !== index &&
                    'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                )}
              >
                {item.icon && (
                  <span className="flex-shrink-0 w-5 h-5 text-gray-400 dark:text-gray-500">
                    {item.icon}
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.label}</p>
                  {item.description && (
                    <p className="text-xs text-gray-500 truncate dark:text-gray-400">
                      {item.description}
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// useCommand Hook
// ============================================================================

export interface UseCommandOptions {
  items?: CommandItem[];
  groups?: CommandGroup[];
  filterFn?: (item: CommandItem, search: string) => boolean;
  onSelect?: (item: CommandItem) => void;
}

export interface UseCommandReturn {
  open: boolean;
  setOpen: (open: boolean) => void;
  search: string;
  setSearch: (search: string) => void;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  filteredItems: CommandItem[];
  handleSelect: (item: CommandItem) => void;
  registerHotkey: (key: string, modifier?: 'meta' | 'ctrl' | 'alt') => void;
}

export function useCommand({
  items = [],
  groups = [],
  filterFn,
  onSelect,
}: UseCommandOptions = {}): UseCommandReturn {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const allItems = React.useMemo(() => {
    const fromGroups = groups.flatMap((g) =>
      g.items.map((item) => ({ ...item, group: g.label }))
    );
    return [...items, ...fromGroups];
  }, [items, groups]);

  const filteredItems = React.useMemo(() => {
    if (!search) return allItems;

    const defaultFilter = (item: CommandItem, query: string) =>
      item.label.toLowerCase().includes(query.toLowerCase()) ||
      item.description?.toLowerCase().includes(query.toLowerCase());

    const filter = filterFn || defaultFilter;
    return allItems.filter((item) => filter(item, search));
  }, [allItems, search, filterFn]);

  React.useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  const handleSelect = React.useCallback(
    (item: CommandItem) => {
      if (item.disabled) return;
      item.action?.();
      onSelect?.(item);
      setOpen(false);
      setSearch('');
    },
    [onSelect]
  );

  const registerHotkey = React.useCallback(
    (key: string, modifier: 'meta' | 'ctrl' | 'alt' = 'meta') => {
      const handleKeyDown = (e: KeyboardEvent) => {
        const modifierPressed =
          (modifier === 'meta' && e.metaKey) ||
          (modifier === 'ctrl' && e.ctrlKey) ||
          (modifier === 'alt' && e.altKey);

        if (modifierPressed && e.key.toLowerCase() === key.toLowerCase()) {
          e.preventDefault();
          setOpen((prev) => !prev);
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    },
    []
  );

  return {
    open,
    setOpen,
    search,
    setSearch,
    selectedIndex,
    setSelectedIndex,
    filteredItems,
    handleSelect,
    registerHotkey,
  };
}
